import type { FC, JSX, MutableRefObject } from 'react';
import type React from 'react';
import type { TanStackColumnDef } from '../../TableComponent.model';
import { type CellProperties } from '@/support/hot.types';
import type { FilterTypes } from '@/constants/FilterTypes';
import type { APITableAsyncExportCallback } from '@/model/AsyncExport.model';
import type { UserTableViewSettings } from '@/model/UserTableView.model';

export const ChangePercentage = {
  FirstTaken: 'value_change_from_first',
  LastTaken: 'value_change',
  TrackingDate: 'value_change_from_tracking_date',
  DiseaseInductionDate: 'value_change_from_disease_induction_date',
} as const;

export type ChangePercentage = (typeof ChangePercentage)[keyof typeof ChangePercentage];

export const DEFAULT_CHANGE_PERCENTAGE: ChangePercentage = ChangePercentage.FirstTaken;

export interface TableFilter {
  category: {
    name: string;
    value: string;
    index: number;
  };
  operation: {
    name: FilterTypes;
    index: number;
  };
  option: {
    name: string;
    value: string;
    from?: string | number;
    to?: string | number;
  };
}

export interface UseTableProps<T> {
  useTableProps: UseAPITableProps<T>;
}

export interface UseAPITableProps<T> {
  apiTable: {
    setUpdating: React.Dispatch<React.SetStateAction<boolean>>;
    fetchTableData: (resetPage?: unknown) => Promise<any>;
    refetchTableData: () => Promise<void>;
    setApiError: (error: Error | false) => void;
    resetTableState: () => void;
    addFilter: (filter: TableFilter) => void;
    replaceFilters: (filters: Array<TableFilter>) => void;
  };
  selectedFlatRows: Array<{ original: T }>;
}

export interface SearchQuery {
  value: string;
  display: string;
}

export type ApiInfo = { type: 'legacyInternalApi'; route: string } | { type: 'internalApi'; route: URL };

// ================================================================
// COLUMN TYPE DEFINITIONS
// ================================================================

/**
 * Type for TanStack Table v8 columns only
 */
export type APITableColumn<T = Record<string, unknown>> = TanStackColumnDef<T>;

export interface APITableProps<Resp> {
  columns: Array<TanStackColumnDef<Resp>>;
  AsideComponent?: FC<UseTableProps<Resp>> | JSX.Element | null;
  NoDataComponent?: FC<UseAPITableProps<Resp>>;
  apiInfo: ApiInfo;
  onFetchTableData?: (data: Array<Resp>, additionalProps?: Record<string, any>) => void;
  filterOptions?: Array<unknown>;
  bulkActions?: (props: UseTableProps<Resp>) => Array<unknown>;
  loadingText?: string;
  canExport?: boolean;
  exportRoutes?: Array<unknown>;
  onAsyncExport?: APITableAsyncExportCallback;
  searchPlaceholderText?: string;
  includeParam?: string;
  searchQueryByOptions?: Array<SearchQuery>;
  showMetricChangeFrom?: boolean;
  changePercentage?: ChangePercentage;
  onChangePercentageChange?: (value: ChangePercentage) => void;
  renderCustomActions?: (props: { selectedFlatRows: any[] }) => React.ReactNode;
  reduxTableName: string;
  hideSearch?: boolean;
  hideFilter?: boolean;
  hideRefreshButton?: boolean;
  defaultSortBy?: { id: string; desc: boolean };
  pageSizes?: Array<number>;
  defaultPageSize?: number;
  excludeParam?: unknown;
  customFilter?: unknown;
  customFilterProcessor?: (filters: Array<unknown>, params: URLSearchParams) => void;
  onSelectRow?: (selectedFlatRows: Array<unknown>, state: CellProperties) => void;
  queryParams?: Record<string, string>;
  refetch?: number | string;
  noDataMessage?: string;
  testIdPrefix?: string;
  loadingClassName?: string;
  maxHeight?: number;
  dataProp?: string;
  defaultFilters?: Array<TableFilter>;
  selectAllOnLoad?: boolean;
  showViewSelector?: boolean;
  /**
   * When true and the left column is empty (no search/filter/bulk actions), render the
   * AsideComponent on the left of the header row alongside the refresh/view selector on
   * the right, keeping the header as a single row.
   */
  asideOnLeft?: boolean;
  /**
   * Ref populated on every render with a function that returns the current column/filter state.
   * Used by parent components that manage their own ViewSelector (e.g. SamplePlanTableViewV2)
   * to read the table's live settings when saving a view.
   */
  getSettingsRef?: MutableRefObject<(() => UserTableViewSettings) | null>;
  /**
   * When set, overrides column/sort/filter state with these settings (e.g. when a parent
   * ViewSelector loads a saved view that includes flat-table column config).
   */
  activeSettingsOverride?: UserTableViewSettings;
}
